/** /notifications
 *
 * Cross-tenant notification management grid, driven by the reusable
 * QuerySearch widget (same `?q=` contract as /delivery-logs/ and /bookings/).
 * A tenant picker (`?tenant=12,845`, one or several operators, or the exclusive
 * "System / global" sentinel `system`) locks the query's tenant. Server-side
 * pagination + sorting; all column filtering is delegated to the widget. Row
 * click / name link opens the editor at /notifications/:id.
 *
 * Batch ops: enable / disable (async 202 → Background Tasks bell). No delete
 * from the grid — deletion is a per-record action on the edit page. No
 * seeded date window (small config table, no partition pruning concern).
 */
import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as nfApi from '@/api/notifications';
import type { NotificationListItem } from '@/api/notifications';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import {
  TenantPicker,
  pickedSentinel,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';

import { CreateNotificationDialog, SYSTEM_TENANT } from './CreateNotificationDialog';
import { buildColumns } from './notificationsColumns';
import { buildNotificationNamespaces } from './notificationsNamespaces';
import { NotificationSummaryPanel } from './NotificationSummaryPanel';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];
/** The picker's sentinel row. Exclusive with the tenants (D3): picked, it is the
 *  whole value. */
const SYSTEM_OPTIONS: TenantPickerValue[] = [SYSTEM_TENANT];

const DEFAULT_HIDDEN = ['mix', 'language_policy', 'block_ref_count', 'tour_override_mode'];

/** Presets store the filter state as the raw `?q=` DSL text (canonical form). */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every tenant-ish condition from the tree (the picker owns tenant). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && (n.namespace === 'tenant' || n.namespace === 'is_system')) {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function NotificationsListPage() {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildNotificationNamespaces(), []);
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  // "12,845" (one or several operators) or the "system" sentinel (null-tenant
  // rows), with the console scope underneath — the page takes the whole scope;
  // the sentinel stays this page's own vocabulary.
  const pageTenant = usePageTenant({ multiple: true });
  const tenantParam = pageTenant.raw;
  const isSystem = tenantParam === 'system';
  // The preset defaults' tenant column: shown unless the page names exactly one
  // operator, or System (every row is tenant-less).
  const defaultShowTenant = !isSystem && pageTenant.ids.length !== 1;

  const presets = useGridPresets({
    grid: 'notifications',
    defaultOrder: useMemo(
      () => buildColumns({ showTenant: defaultShowTenant }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const scopeYields = pageTenant.fromScope && queryNamesTenant;
  /** The picker's value: the operators, or exactly `[System]`. */
  const tenants = isSystem ? SYSTEM_OPTIONS : scopeYields ? NO_TENANTS : pageTenant.values;
  const operatorIds = isSystem ? null : formatTenantIds(tenants.map((t) => t.id));
  // The create dialog pre-selects / features the page's effective tenants (D9);
  // the `system` sentinel names no operator and opens it on System instead.
  const pageTenants = pageTenantsFrom(operatorIds, tenants);
  /** What the API's tour_operator_id param should carry: `'12,845'` or `system`. */
  const tenantApiValue: string | undefined = isSystem ? 'system' : (operatorIds ?? undefined);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, tenantParam]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // The picker is authoritative for tenant — strip tenant/is_system chips.
  // Never for a scope-derived tenant: the console scope must not rewrite the
  // user's (or a preset's) query — that would also diverge a loaded preset.
  useEffect(() => {
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const { setRaw: setTenantParam } = pageTenant;
  const onTenantChange = useCallback(
    (next: TenantPickerValue[]) =>
      setTenantParam(
        pickedSentinel(next, SYSTEM_OPTIONS) ? 'system' : formatTenantIds(next.map((t) => t.id)),
      ),
    [setTenantParam],
  );

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The tenant suggestions are the bookings autocomplete, which takes the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'notifications' });
  const query = useQuery({
    queryKey: [
      'notifications',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
    ],
    queryFn: () =>
      nfApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        filters: toWireAst(tree),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking,
  });

  // ------------------------------------------------------------------------
  // Batch enable/disable — hand-wired (raw DataGridPro), integer row ids.
  // ------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);
  const rowCount = query.data?.count ?? 0;

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue ?? null, ordering }),
    [q, tenantApiValue, ordering],
  );
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(() => selectionModel.map((i) => Number(i)), [selectionModel]);
  const selectedCount = allMatching ? rowCount : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'notifications',
    queryKey: ['notifications'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  // Declared unconditionally; BatchActionsMenu filters by `permission`.
  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'enable',
        label: 'Enable',
        action: 'enable',
        permission: 'console_notifications_manage',
        confirmText: (n) =>
          `Enable ${n.toLocaleString()} notification(s)? They will start firing on matching events. Runs in the background.`,
      },
      {
        key: 'disable',
        label: 'Disable',
        action: 'disable',
        permission: 'console_notifications_manage',
        destructive: true,
        confirmText: (n) =>
          `Disable ${n.toLocaleString()} notification(s)? Delivery stops immediately. Runs in the background.`,
      },
    ],
    [],
  );

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({ showTenant: !isSystem && tenants.length !== 1 }),
        presets.orderedFields,
      ),
    [isSystem, tenants.length, presets.orderedFields],
  );

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<NotificationListItem>) => (
      <Box sx={{ p: 2 }}>
        <NotificationSummaryPanel row={row} />
      </Box>
    ),
    [],
  );
  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="notification-grid-editor"
        title="Notifications"
        subtitle="Search, edit and enable/disable event notifications across every tenant."
        actions={
          <PermissionGate perm="console_notifications_manage">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Add notification
            </Button>
          </PermissionGate>
        }
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={onTenantChange}
            extraOptions={SYSTEM_OPTIONS}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder='Search notifications…  e.g. event=~departure & template=~"{% if"'
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<NotificationListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={rowCount}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          getRowHeight={() => 'auto'}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          onRowClick={({ row }) => navigate(`/notifications/${row.id}`)}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnFilter
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          keepNonExistentRowsSelected
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch ? 'No notifications match this search.' : 'No notifications found.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'notifications',
              presets: presets.toolbar,
              batch: {
                actions: batchActions,
                ids: selectedIds,
                allMatching,
                count: selectedCount,
                matched: rowCount,
                filterQuery,
                runStandard,
                clearSelection,
                onSelectAllMatching: () => setAllMatching(true),
                isPending: batchAction.isPending,
              },
            } as GridToolbarProps,
          }}
        />
      )}

      {createOpen && (
        <CreateNotificationDialog
          open
          pageTenants={pageTenants}
          defaultSystem={isSystem}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </Box>
  );
}
